import { DatePipe } from '@angular/common';
import { Component, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ArquivoTransparencia, BaseResourceListComponent, Coluna, Filtro, GlobalService, GrupoArquivoTransparencia, LoginContabil } from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { tsXLXS } from 'ts-xlsx-export';

import { error, info, warning } from 'toastr';
import { ArquivoTransparenciaService } from '../service/arquivo-transparencia.service';
import { TransparenciaStorageService } from '../service/transparencia-storage.service';
import { GrupoArquivoTransparenciaService } from '../service/grupo-arquivo-transparencia.service';

declare var $: any;

@Component({
  selector: 'app-transparencia-arquivo-emenda-adiantamento',
  templateUrl: './transparencia-arquivo-emenda-adiantamento.component.html'
})
export class TransparenciaArquivoEmendaAdiantamentoComponent extends BaseResourceListComponent<ArquivoTransparencia, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  public ptBR: any;
  public datepipe: DatePipe;
  public tipo: 'EMENDA' | 'ADIANTAMENTO';

  public listaAnexos = new Array<any>();

  public entidade = new ArquivoTransparencia();
  public uploadedFiles: any[] = [];

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    private route: ActivatedRoute,
    protected injector: Injector,
    private messageService: MessageService,
    private globalService: GlobalService,
    private anexoService: ArquivoTransparenciaService,
    private transparenciaStorageService: TransparenciaStorageService,
    private grupoArquivoService: GrupoArquivoTransparenciaService,
  ) {
    super(anexoService, injector);
    this.tipo = this.route
      .snapshot
      .routeConfig
      .path
      .includes('emendas')
        ? 'EMENDA'
        : 'ADIANTAMENTO';
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected relations(): string {
    return 'grupo.orgao';
  }

  protected condicoesGrid(): {} {
    const parametros = {};
    this.datepipe = new DatePipe('pt');
    parametros['ignoreCondObrig'] = true;
    parametros['grupo.orgao.id'] = this.login.orgao.id;
    parametros['grupo.nome'] = 'ARQUIVOS';
    parametros['grupo.descricao'] = this.tipo;
    return parametros;
  }

  protected ordenacaoGrid(): string[] {
    return ['id$DESC'];
  }

  protected filtrosGrid(): Filtro {
    return {
      number: ['referencia', 'tamanho'],
      text: ['descricao', 'nome', 'tipo', 'referencia', 'grupo.nome']
    };
  }

  public beforeInit(): void {
    this.entidade.grupo = new GrupoArquivoTransparencia();
  }

  protected afterInit(): void {
    window.scrollTo(0, 0);
    this.ptBR = this.globalService.obterDataBR();
  }

  protected acaoRemover(model: ArquivoTransparencia): Observable<ArquivoTransparencia> {
    return null;
  }

  public obterParametros() {
    const parametros = super.obterParametros();
    parametros['relations'] = this.relations();

    switch (this.col) {
      case 1:
        parametros['orderBy'] = `referencia$${this.ascendente ? 'ASC' : 'DESC'}`;
        break;
      case 2:
        parametros['orderBy'] = `descricao$${this.ascendente ? 'ASC' : 'DESC'}`;
        break;
      case 3:
        parametros['orderBy'] = `nome$${this.ascendente ? 'ASC' : 'DESC'}`;
        break;
      case 4:
        parametros['orderBy'] = `grupo.nome$${this.ascendente ? 'ASC' : 'DESC'}`;
        break;
      case 5:
        parametros['orderBy'] = `tamanho$${this.ascendente ? 'ASC' : 'DESC'}`;
        break;
      default:
        break;
    }
    return parametros;
  }

  public preencherGrid() {
    const parametros = this.obterParametros();
    this.anexoService
      .filtrar(
        this.paginaCorrente,
        this.limite,
        parametros
      )
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        lista => {
          this.lista = lista.content;
          this.paginaTotal = lista.totalPages;
        },
        () => alert('erro ao retornar lista')
      );
    this.transparenciaStorageService
      .filtrar(
        this.paginaCorrente,
        this.limite,
        parametros
      )
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        lista => {
          this.lista = this.lista.concat(lista.content);
          this.paginaTotal += lista.totalPages;
        },
        () => alert('erro ao retornar lista')
      );
  }

  protected colunasRelatorio(): string[] | Coluna[] {
    return [
      { titulo: 'Nome', coluna: 'nome' },
      { titulo: 'Descrição', coluna: 'descricao' },
      { titulo: 'Referência', coluna: 'referencia' },
      { titulo: 'Tamanho', coluna: 'tamanho' },
      { titulo: 'Grupo', coluna: 'grupo.nome' },
      { titulo: 'Caminho', coluna: 'caminho' }
    ];
  }

  public imprimirPDF() {
    this.imprimir(
      `LISTAGEM DE ANEXOS DE ${this.tipo}`,
      this.login.usuario.nome, this.login.usuario.sobrenome,
      this.login.orgao.nome, this.login.brasao, 'landscape',
      'Listagem anexos', ['auto', '*', 'auto', 'auto', 'auto', 'auto'], this.lista
    );
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  public exportarXLSX() {
    const listaItens = new Array();
    for (const item of this.lista) {
      const entity = {
        nome: item.nome,
        descricao: item.descricao,
        tamanho: item.tamanho,
        referencia: item.referencia,
        grupo: item.grupo.nome,
        caminho: item.caminho
      };
      listaItens.push(entity);
    }
    tsXLXS().exportAsExcelFile(listaItens).saveAsExcelFile('anexos');
  }

  public download(arquivo: any) {
    this.anexoService
      .download(arquivo.documento).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        const blob = new Blob([res], { type: arquivo.tipo });
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = arquivo.nome;
        link.click();
        window.URL.revokeObjectURL(downloadURL);
      });
  }

  public remover(item: any) {
    if (item.id && item.caminho) {
      this.anexoService.remover(item.id).pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          this.messageService.add({ severity: 'info', summary: 'Exclusão', detail: 'Registro removido com sucesso!' });
          this.preencherGrid()
        });
    }
    if (item.id && item.url) {
      this.transparenciaStorageService.remover(item.id).pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          info('Registro removido com sucesso!', 'Exclusão');
          this.lista.splice(this.lista.indexOf(item), 1);
        }, (err) => error(err.error.payload));
    }
  }

  public editar(item: GrupoArquivoTransparencia) {
    item['editavel'] = true;
  }

  public cancelar(item: GrupoArquivoTransparencia) {
    item['editavel'] = false;
  }

  public salvar(item: GrupoArquivoTransparencia) {
    item['editavel'] = false;
  }

  public inserirArquivo(tipo: 'ADIANTAMENTO' | 'EMENDA') {
    this.grupoArquivoService
      .obter({ nome: 'ARQUIVOS', descricao: tipo, orgao_id: this.login.orgao.id })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(grupo => {
        if (!grupo) return;

        this.entidade = new ArquivoTransparencia();
        this.entidade.grupo = grupo;
        this.uploadedFiles = [];

        $('#dialogUploadArquivo').modal('show');
      })
  }

  public async onUpload(event: any, fileUpload: any) {
  
    if (!this.entidade.descricao || this.entidade.descricao === '') {
      warning('Informe a descrição do arquivo!');
      return;
    }

    if (!this.entidade.grupo.id) {
      warning('Selecione o grupo!');
      return;
    }

    if (!event.files || event.files.length === 0) {
      warning('Selecione o(s) arquivo(s)');
      return;
    }

    try {
      for (const file of event.files) {
        this.uploadedFiles.push(file);
      }

      this.transparenciaStorageService
        .upload(
          this.uploadedFiles,
          this.login.orgao.id,
          this.entidade.grupo.id,
          this.entidade.descricao,
          null,
          `ANEXOS ${this.entidade?.grupo?.descricao}`
        ).pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          for (const item of res) {
            item.data_cadastro = new Date();
          }

          this.preencherGrid();
        }, (res) => {
          error(res.error.payload);
          this.uploadedFiles.length = 0;
        });

    } catch (ex) {
      error(ex.message ? ex.message : ex);
    }

    this.uploadedFiles = [];
    fileUpload.clear();
    $('#dialogUploadArquivo').modal('hide');
  }
}
